/**
 * Home Page - Climadiag Home Page
 * Modern version with Vite + React 19 + Material-UI v7
 */
import React from 'react';
import { useNavigate } from 'react-router';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';

import Layout from '../components/Layout';
import Search, { SEARCH_INPUT_LABEL } from '../components/Search';
import OutlookSwitcher from '../components/OutlookSwitcher';

import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import useInteractiveIframe from '../hooks/ui/useInteractiveIframe';

// Import des assets
import homeBackground from '../assets/images/home_background.png';
import logoClimadiag from '../assets/images/logo_Blanc_Climadiag.png';
import francesadapte from '../assets/images/france-s-adapte.png';

// Import de la configuration centralisée
import { homePage } from '../config/settings';

// SSR-compatible iframe detection
const getInFrame = () => {
  if (typeof globalThis === 'undefined') return false;
  return globalThis.parent !== globalThis;
};

/**
 * HomePage Component
 * Home page with search, outlook switcher and responsive design
 * @returns {JSX.Element} Home page component
 */
const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const replaceTokens = useTokenReplacement();

  // Iframe hook for parent-child communication
  const { pathname = '/', search = '', hash = '' } = globalThis.location || {};
  const inFrame = getInFrame();

  // Conditional iframe hook usage
  useInteractiveIframe({
    key: 'homepage',
    location: { pathname, search, hash }
  });

  /**
   * Entity selection handler from search
   * @param {Object} entity - Selected entity
   */
  const handleEntitySelect = (entity) => {
    if (entity?.identifiant) {
      navigate(`/entity/${entity.identifiant}`);
    }
  };

  return (
    <Layout
      title="Climadiag Commune / Météo France"
      hasHeader={false}
      maxWidth="unset"
      sx={{
        height: inFrame ? 'auto' : '100vh',
        padding: '50px',
        background:
          'radial-gradient(circle at calc(50% + 150px) 500px, rgba(46,170,225,1) 1%, rgba(46,170,225,1) 10%, rgba(16,79,144,1) 40%, rgba(16,79,144,1) 100%)',
        [theme.breakpoints.down('sm')]: {
          padding: '10px',
          background:
            'radial-gradient(circle at 50% 300px, rgba(46,170,225,1) 1%, rgba(46,170,225,1) 10%, rgba(16,79,144,1) 40%, rgba(16,79,144,1) 100%)',
        },
        color: 'white',
        width: '100%',
      }}
    >
      {/* Header avec logo et recherche */}
      <Stack
        sx={{
          flexDirection: 'row',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
          },
        }}
        width="100%"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            maxWidth: '50%',
            textTransform: 'uppercase',
            fontWeight: 600,
            whiteSpace: 'pre-wrap',
            marginTop: 2,
            [theme.breakpoints.down('sm')]: {
              maxWidth: '100%',
            },
          }}
        >
          {replaceTokens(homePage.subtitle)}
        </Typography>

        {/* Zone recherche et outlook */}
        <Stack
          sx={{
            width: 500,
            [theme.breakpoints.down('sm')]: {
              width: '100%',
              pt: 5,
            },
          }}
        >
          {/* Climate outlook selector */}
          <OutlookSwitcher variant="home" />

          {/* Search component */}
          <Search
            onSelectionChange={handleEntitySelect}
            hideDropdownIcon={true}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={homePage.searchText}
                variant="standard"
                inputProps={{
                  ...params.inputProps,
                  'aria-label': SEARCH_INPUT_LABEL,
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 40,
                  width: '100%',
                  // Focus ring on the rounded wrapper: the input itself is
                  // transparent and square, so it cannot carry one that matches
                  // the visible field. Browser "auto" outline, as on buttons.
                  '&:has(input:focus-visible)': {
                    outline: 'auto',
                    outlineOffset: '2px',
                  },
                  '& .MuiInput-root': {
                    height: '70px',
                    [theme.breakpoints.down('sm')]: {
                      height: '50px',
                    },
                    width: '100%',
                    paddingLeft: 2,
                    '&:before, &:after': {
                      display: 'none',
                    },
                  },
                  '& .MuiInput-input': {
                    padding: '0 8px',
                  },
                }}
              />
            )}
          />
        </Stack>
      </Stack>

      {/* Section titre et logo France s'adapte */}
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ mt: 5 }} alignItems="center">
        {/* Logo Climadiag */}
        <Box
          sx={{
            width: 250,
            [theme.breakpoints.down('sm')]: {
              width: '80%',
            },
          }}
          component="img"
          src={logoClimadiag}
          alt="Logo Climadiag"
        />

        <Box
          sx={{
            ml: 'auto',
            mr: { xs: 'auto', sm: 4 },
            height: 80,
            display: 'block',
          }}
          component="img"
          src={francesadapte}
          alt="France s'adapte"
        />
      </Stack>

      {/* Image de background principale */}
      <Box
        component="img"
        src={homeBackground}
        alt="Illustration climat"
        sx={{
          width: '100%',
          maxWidth: '1000px',
          display: 'block',
          [theme.breakpoints.down('sm')]: {
            mt: 5,
          },
          ml: 'auto',
          mr: 'auto',
        }}
      />
    </Layout>
  );
};

export default HomePage;
